/**
 * Warteschlange Punkt 94: Rückfrage beim Absagen eines Vorgangs mit UNTERSCHRIEBENER
 * Kostenübernahme. Das System entscheidet nichts selbst — drei Antworten OHNE Vorauswahl,
 * Pflicht-Begründung, der Knopf wird erst aktiv, wenn beides gesetzt ist. Die Lage (unterschrieben
 * am, Betrag, Stornobedingungen) kommt vom Server (422 `kostenuebernahme_entscheidung_fehlt`,
 * `apps/api/src/lib/absage-kostenuebernahme.ts`) — der Dialog rechnet nichts selbst.
 */
import { useLayoutEffect, useState } from "react";
import { Button, ErrorNote, Icon, Modal, Radio } from "./components";
import { formatDate, money } from "./format";
import { KOSTENUEBERNAHME_ENTSCHEIDUNG_LABEL, type KostenuebernahmeEntscheidung } from "../lib/activity-labels";

export interface KostenuebernahmeLage {
  unterschriebenAm: string | null;
  betragBruttoCents: number | null;
  stornobedingungen: string | null;
  stornobedingungenQuelle: "vertrag" | "betrieb" | null;
}

export function AbsageKostenuebernahmeDialog({
  lage,
  onClose,
  onAbsagen,
}: {
  /** null = geschlossen. */
  lage: KostenuebernahmeLage | null;
  onClose: () => void;
  /** Liefert eine Fehlermeldung oder null bei Erfolg. */
  onAbsagen: (entscheidung: KostenuebernahmeEntscheidung, grund: string) => Promise<string | null>;
}) {
  const [entscheidung, setEntscheidung] = useState<KostenuebernahmeEntscheidung | null>(null);
  const [grund, setGrund] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (lage) {
      setEntscheidung(null);
      setGrund("");
      setFehler(null);
    }
  }, [lage]);

  const bereit = entscheidung !== null && grund.trim().length >= 3 && !busy;

  async function absagen() {
    if (!entscheidung || !bereit) return;
    setBusy(true);
    setFehler(null);
    const f = await onAbsagen(entscheidung, grund.trim());
    setBusy(false);
    if (f) setFehler(f);
  }

  const bedingungenTitel =
    lage?.stornobedingungenQuelle === "vertrag" ? "Stornobedingungen laut Vertrag" : "Stornobedingungen des Betriebs (im Vertrag stand keine)";

  return (
    <Modal
      open={lage !== null}
      onClose={onClose}
      width="520px"
      title="Kostenübernahme ist unterschrieben"
      subtitle="Wie soll mit der Absage umgegangen werden? Es wird nichts automatisch verrechnet."
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" data-testid="ku-absagen" onClick={() => void absagen()} disabled={!bereit}>
            Vorgang absagen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <dl data-testid="ku-lage" className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12.5px]">
          <dt className="text-secondary-fixed-dim">Unterschrieben am</dt>
          <dd className="font-semibold text-on-background dark:text-inverse-on-surface">{lage?.unterschriebenAm ? formatDate(lage.unterschriebenAm) : "–"}</dd>
          <dt className="text-secondary-fixed-dim">Betrag</dt>
          <dd className="font-semibold text-on-background dark:text-inverse-on-surface">{lage?.betragBruttoCents != null ? money(lage.betragBruttoCents) : "–"}</dd>
        </dl>
        <div>
          <p className="mb-1 text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">{bedingungenTitel}</p>
          <p className="whitespace-pre-wrap rounded-[10px] bg-surface-container-low px-3 py-2 text-[12.5px] text-on-surface-variant dark:bg-night-card">
            {lage?.stornobedingungen ?? "Keine Stornobedingungen hinterlegt."}
          </p>
        </div>

        <fieldset>
          <legend className="mb-1.5 text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Entscheidung</legend>
          <div className="space-y-1.5">
            {(Object.keys(KOSTENUEBERNAHME_ENTSCHEIDUNG_LABEL) as KostenuebernahmeEntscheidung[]).map((k) => (
              <label key={k} className="flex items-center gap-2.5 text-[13px] text-on-background dark:text-inverse-on-surface">
                <Radio
                  name="ku-entscheidung"
                  value={k}
                  checked={entscheidung === k}
                  onChange={() => setEntscheidung(k)}
                  disabled={busy}
                />
                {KOSTENUEBERNAHME_ENTSCHEIDUNG_LABEL[k]}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Begründung (Pflicht)</span>
          <textarea
            data-testid="ku-grund"
            className="min-h-[80px] w-full rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest p-2.5 text-[12.5px] dark:border-outline/40 dark:bg-night-card"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            maxLength={2000}
            disabled={busy}
          />
        </label>
        {fehler && <ErrorNote message={fehler} />}
      </div>

    </Modal>
  );
}
